/**
 * This is the entry point for Feature Hub App integration
 */

import React, { StrictMode } from 'react';
import { UniversalEditorProvider } from '@oneaudi/falcon-tools';
import type { FeatureAppDefinition, FeatureAppEnvironment } from '@feature-hub/core';
import type { ReactFeatureApp } from '@feature-hub/react';
import type { TrackingServiceV2, ContentServiceV1 } from './feature-service-types';

import { FeatureApp } from './FeatureApp';
import { getDesignLibrary } from '../alpha-helper';

const featureAppDefinition: FeatureAppDefinition<ReactFeatureApp, FeatureServiceDependencies> = {
  dependencies: {
    featureServices: {
      'audi-content-service': '^1.0.0',
      'dbad:audi-i18n-service': '^1.0.0',
      'locale-service': '^1.0.0',
      'audi-footnote-reference-service': '^3.0.0',
      'dbad:audi-vue-formatter-service': '^1.0.0',
    },
    externals: {
      '@feature-hub/react': '*',
      react: '*',
      'styled-components': '*',
      ...getDesignLibrary({ unifiedWebCommonVersion: '*', unifiedWebAlphaCommonVersion: '*' }),
    },
  },
  optionalDependencies: {
    featureServices: {
      'audi-tracking-service': '^2.0.0',
      's2:async-ssr-manager': '^1.0.0',
      's2:logger': '^1.0.0',
      's2:serialized-state-manager': '^1.0.0',
      'audi-render-mode-service': '^1.0.0',
    },
  },

  create: ({ featureServices }: FeatureAppEnvironment<FeatureServiceDependencies, void>) => {
    const serviceLocale = featureServices['locale-service'];

    const logger = featureServices['s2:logger'];
    logger?.info('👺 Feature App created.');

    const serviceTracking = featureServices['audi-tracking-service'];
    if (serviceTracking) {
      serviceTracking.featureAppName = 'fa-carousel';
    }

    const serviceContent = featureServices['audi-content-service'];
    const serviceFootnote = featureServices['audi-footnote-reference-service'];

    const vueFormatterService = featureServices['dbad:audi-vue-formatter-service'];

    const serviceI18n = featureServices['dbad:audi-i18n-service'];
    const serviceSerializedStateManager = featureServices['s2:serialized-state-manager'];
    const serviceAsyncSsrManager = featureServices['s2:async-ssr-manager'];

    if (serviceAsyncSsrManager) {
      // This 3 step process inspired by this link:
      // https://github.com/oneaudi/i18n-context?tab=readme-ov-file#featureapp-definition
      // Was getting TS errors by adding `async` within `scheduleRerender`
      const waitForThese = async () => {
        await serviceI18n.setLocale();
        const content = (await serviceContent?.getContent()) as Record<string, unknown>;
        serviceSerializedStateManager?.register(() => JSON.stringify(content));
      };
      const promiseWaitForThese = waitForThese();
      serviceAsyncSsrManager.scheduleRerender(promiseWaitForThese);
    }

    return {
      render: () => {
        return (
          <UniversalEditorProvider
            contentService={serviceContent as ContentServiceV1}
            renderModeService={featureServices['audi-render-mode-service']}
          >
            <StrictMode>
              <FeatureApp
                serviceFootnote={serviceFootnote}
                serviceLocale={serviceLocale}
                serviceI18n={serviceI18n}
                serviceTracking={serviceTracking as TrackingServiceV2}
                serviceContent={serviceContent as ContentServiceV1}
                serviceVueFormatter={vueFormatterService}
              />
            </StrictMode>
          </UniversalEditorProvider>
        );
      },
    };
  },
};

export default featureAppDefinition;
